import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  AccessComponentEnum,
  AccessLevelEnum,
  FilterGroupingEnum,
  FormTemplate,
  FormTemplateFilterInputObject,
  FormTemplateScopeNameEnum,
  CompanyPlanUpgradeFeatureEnum,
} from 'graphql-common';
import Table from '@lib/components/Table/Table';
import Search from '@lib/components/Search/Search';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import ListEmptyState from '@lib/components/ListEmptyState/ListEmptyState';
import FeatureLockState from '@lib/components/FeatureLockState';
import Trans from '@lib/components/Trans/Trans';
import { IconButtonTypes } from '@lib/components/IconButton/IconButton';
import { PlanState } from '@lib/enums/plan';
import { TableAction } from '@lib/components/Table/types';
import useFormTemplatesQueryHook from 'utils/fetch/hooks/useFormTemplatesQueryHook';
import { APP_URLS } from 'constants/urls';
import useTabListState from '@lib/components/Table/utils/useTabListState';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import getFormTemplatesColumns from './utils/getFormTemplatesColumns';

interface FormTemplatesTabProps {
  documentId: string;
}

export default function FormTemplatesTab({
  documentId,
}: FormTemplatesTabProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { listState, params, searchQuery, onSearchChange } = useTabListState();
  const { getUserAccessByCompany, handleOpenPlanModal }: ContextProps =
    useOutletContext();
  const isPlanLocked =
    getUserAccessByCompany(AccessComponentEnum.DocumentationManagement) ===
    AccessLevelEnum.LimitedRead;

  const additionalFilters: FormTemplateFilterInputObject[] = useMemo(
    () => [
      {
        documentationsId: {
          grouping: FilterGroupingEnum.And,
          predicate: { eq: documentId },
          filterGroup: 'documentationsId',
        },
      },
    ],
    [documentId],
  );

  const {
    formTemplates,
    formTemplatesFirstLoading,
    formTemplatesLoading,
    totalFormTemplatesCount,
  } = useFormTemplatesQueryHook({
    additionalFilters,
    paginationParams: params,
    searchQuery,
    skip: isPlanLocked,
    tabParam: FormTemplateScopeNameEnum.Active,
  });

  const onViewHandler = useCallback(
    (item: FormTemplate) => {
      const versionId = item.currentVersion?.id || '';
      navigate(
        APP_URLS.dashboard.formTemplates.viewVersion.getDynamicPath({
          pathParts: { id: item.id, versionId },
        }),
      );
    },
    [navigate],
  );

  const columns = useMemo(() => getFormTemplatesColumns({ t }), [t]);

  const tableActions: TableAction<FormTemplate>[] = useMemo(
    () => [
      {
        id: 'view-action',
        icon: 'visibility',
        onClick: onViewHandler,
        type: IconButtonTypes.PrimaryOutlined,
        predictorFnTooltipProps: () => ({ body: t('view') }),
      },
    ],
    [onViewHandler, t],
  );

  if (isPlanLocked) {
    return (
      <FeatureLockState
        title={t('unlock-documentation-in-forms')}
        text={<Trans i18nKey="unlock-documentation-in-forms-text" />}
        onUpgrade={() =>
          handleOpenPlanModal(PlanState.Locked, [
            CompanyPlanUpgradeFeatureEnum.Documentation,
          ])
        }
      />
    );
  }

  if (formTemplatesFirstLoading) {
    return <CircleLoader />;
  }

  const emptyState = (
    <ListEmptyState
      title={t('no-linked-form-templates')}
      text={t('no-linked-form-templates-text')}
    />
  );

  return (
    <>
      {((!!totalFormTemplatesCount && !!formTemplates.length) ||
        searchQuery) && (
        <Search
          placeholder={t('form-templates-search-placeholder')}
          value={searchQuery}
          onChange={onSearchChange}
          useSearchParam={false}
        />
      )}
      <Table
        actions={tableActions}
        columns={columns}
        counterText={t('form-template-plural', {
          count: totalFormTemplatesCount,
        })}
        data={formTemplates}
        emptyState={emptyState}
        id="documentFormTemplates"
        loading={formTemplatesLoading}
        orderingState={listState}
        paginationState={listState}
        totalEntries={totalFormTemplatesCount}
      />
    </>
  );
}
